import {useState} from 'react';
import {useParams, Link, useNavigate} from 'react-router-dom';
import {format, formatDistanceToNow} from 'date-fns';
import {
  Ticket as TicketIcon,
  ArrowLeft,
  ExternalLink,
  Activity,
  MessageSquare,
  Paperclip,
  Shield,
} from 'lucide-react';
import type {PortalTicketStatus, PortalTicketActivityAction} from '@api/generated/client';
import {trpc} from '@common-web/lib/trpc';
import {
  STATUS_LABELS,
  STATUS_BADGE_CLASS,
  TYPE_LABELS,
  PRIORITY_LABELS,
  SEND_BACKABLE,
  SEND_TO_DEV_FROM,
  ENGINEERING_STATUS_OPTIONS,
  ENGINEERING_STATUS_MANAGEABLE_FROM,
} from '@common-web/components/tickets/constants';
import {useToast} from '@common-web/hooks/use-toast';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Button} from '@common-web/components/ui/button';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Textarea} from '@common-web/components/ui/textarea';
import {Label} from '@common-web/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';

type EngineeringStatus = (typeof ENGINEERING_STATUS_OPTIONS)[number];

const ACTION_LABELS: Record<PortalTicketActivityAction, string> = {
  CREATED: 'created the ticket',
  STATUS_CHANGE: 'changed the status',
  COMMENT_ADDED: 'commented',
  ATTACHMENT_ADDED: 'added an attachment',
  CLARIFICATION_REQUESTED: 'requested clarification',
  CLARIFICATION_PROVIDED: 'provided clarification',
  EDITED: 'edited the ticket',
  MARKED_DUPLICATE: 'marked as duplicate',
  RESOLVED: 'resolved the ticket',
  ACCEPTED: 'accepted the resolution',
  REOPENED: 'reopened the ticket',
  ESCALATED: 'escalated for review',
  SENT_BACK: 'sent the ticket back',
  FORWARDED_TO_CB: 'forwarded to Clever Benefits',
  CB_STATUS_CHANGE: 'changed the dev-side status',
  ASANA_SYNC: 'synced with Asana',
  REMINDER_SENT: 'sent a reminder',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function activityMetadataText(
  action: PortalTicketActivityAction,
  metadata: unknown,
): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const m = metadata as Record<string, unknown>;
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
  if (action === 'CLARIFICATION_REQUESTED') return str(m.question);
  if (action === 'CLARIFICATION_PROVIDED') return str(m.response);
  if (action === 'RESOLVED') return str(m.message);
  if (action === 'REOPENED') return str(m.reason);
  if (action === 'SENT_BACK') return str(m.reason);
  if (action === 'FORWARDED_TO_CB') return str(m.notes);
  if (action === 'EDITED') {
    const changes = m.changes;
    if (!changes || typeof changes !== 'object') return null;
    const fields = Object.keys(changes);
    return fields.length > 0 ? `Updated ${fields.join(', ')}` : null;
  }
  if (action === 'MARKED_DUPLICATE') {
    const number = str(m.duplicateOfNumber);
    const note = str(m.note);
    if (number && note) return `Duplicate of ${number} — ${note}`;
    if (number) return `Duplicate of ${number}`;
    return note;
  }
  return null;
}

export function InternalTicketDetail() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {toast} = useToast();
  const utils = trpc.useUtils();

  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [reason, setReason] = useState('');

  const [sendToDevOpen, setSendToDevOpen] = useState(false);
  const [sendToDevCategory, setSendToDevCategory] = useState<'PROJECT' | 'ON_CALL'>('ON_CALL');
  const [sendToDevNotes, setSendToDevNotes] = useState('');

  const ticketQuery = trpc.internal.tickets.get.useQuery(
    {id: id ?? ''},
    {enabled: !!id},
  );

  const sendBack = trpc.internal.tickets.sendBackToSuperAdmin.useMutation({
    onSuccess: () => {
      toast({title: 'Sent back to client admin'});
      setSendBackOpen(false);
      setReason('');
      void utils.internal.tickets.invalidate();
      navigate('/tickets');
    },
    onError: (err) =>
      toast({title: 'Action failed', description: err.message, variant: 'destructive'}),
  });

  const sendToDev = trpc.internal.tickets.sendToDev.useMutation({
    onSuccess: () => {
      toast({title: 'Sent to dev backlog'});
      setSendToDevOpen(false);
      setSendToDevNotes('');
      void utils.internal.tickets.invalidate();
    },
    onError: (err) =>
      toast({title: 'Action failed', description: err.message, variant: 'destructive'}),
  });

  const updateCategory = trpc.internal.tickets.updateEngineeringCategory.useMutation({
    onSuccess: () => {
      toast({title: 'Category updated'});
      void utils.internal.tickets.invalidate();
    },
    onError: (err) =>
      toast({title: 'Update failed', description: err.message, variant: 'destructive'}),
  });

  const updateStatus = trpc.internal.tickets.updateStatus.useMutation({
    onSuccess: (ticket) => {
      toast({title: `Status updated to ${STATUS_LABELS[ticket.status]}`});
      void utils.internal.tickets.invalidate();
    },
    onError: (err) =>
      toast({title: 'Action failed', description: err.message, variant: 'destructive'}),
  });

  if (ticketQuery.isLoading || !ticketQuery.data) {
    return (
      <div className='p-6 space-y-4'>
        <Skeleton className='h-8 w-1/3' />
        <Skeleton className='h-4 w-full' />
        <Skeleton className='h-4 w-full' />
        <Skeleton className='h-40 w-full' />
      </div>
    );
  }

  const t = ticketQuery.data;

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <PageHeader
        title={t.title}
        description={`${t.ticketNumber} — ${t.client.name}`}
        icon={<TicketIcon className='h-6 w-6' />}
      >
        <Badge className={STATUS_BADGE_CLASS[t.status]}>{STATUS_LABELS[t.status]}</Badge>
        <Badge variant='outline'>{TYPE_LABELS[t.type]}</Badge>
        {t.priority && <Badge variant='outline'>{PRIORITY_LABELS[t.priority]} priority</Badge>}
        {t.reopenCount > 0 && <Badge variant='outline'>Reopened ×{t.reopenCount}</Badge>}
        <PageHeaderAction tone='secondary' asChild>
          <Link to='/tickets'>
            <ArrowLeft className='h-4 w-4 mr-1' />
            All Clever Benefits tickets
          </Link>
        </PageHeaderAction>
      </PageHeader>

      <div className='max-w-6xl mx-auto px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <div className='lg:col-span-2 space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='text-sm whitespace-pre-wrap text-brand-navy/90'>{t.description}</div>
            </CardContent>
          </Card>

          {t.comments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='text-base flex items-center gap-2'>
                  <MessageSquare className='h-5 w-5 text-brand-blue' />
                  Comments ({t.comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                {t.comments.map((c) => (
                  <div
                    key={c.id}
                    className={`p-3 rounded border ${
                      c.internal ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className='flex items-center gap-2 text-xs text-brand-gray mb-1'>
                      <span className='font-medium text-brand-navy'>
                        {c.author.name ?? c.author.email}
                      </span>
                      <span>·</span>
                      <span>{format(new Date(c.createdAt), "MMM d 'at' h:mm a")}</span>
                      {c.internal && (
                        <Badge className='bg-amber text-white border-0 text-[10px] px-1.5 py-0'>
                          Internal
                        </Badge>
                      )}
                    </div>
                    <div className='text-sm whitespace-pre-wrap'>{c.content}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {t.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='text-base flex items-center gap-2'>
                  <Paperclip className='h-5 w-5 text-brand-blue' />
                  Attachments ({t.attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-2'>
                {t.attachments.map((a) => (
                  <div
                    key={a.id}
                    className='flex items-center justify-between p-2 rounded-sm border bg-gray-50 text-sm'
                  >
                    <div className='min-w-0'>
                      <div className='font-medium text-brand-navy truncate'>{a.fileName}</div>
                      <div className='text-xs text-brand-gray'>
                        {formatBytes(a.fileSize)} · {a.uploadedBy.name ?? a.uploadedBy.email} ·{' '}
                        {formatDistanceToNow(new Date(a.createdAt), {addSuffix: true})}
                      </div>
                    </div>
                  </div>
                ))}
                <div className='text-xs text-muted-foreground'>
                  Download from the client portal — internal portal doesn't currently sign S3 URLs.
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className='text-base flex items-center gap-2'>
                <Activity className='h-5 w-5 text-brand-blue' />
                Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className='relative border-l border-gray-200 ml-2 space-y-4'>
                {t.activity.map((a) => {
                  const detail = activityMetadataText(a.action, a.metadata);
                  return (
                    <li key={a.id} className='ml-4 relative'>
                      <span className='absolute left-[-1.4rem] top-1.5 w-2.5 h-2.5 bg-brand-blue/60 rounded-full ring-4 ring-white' />
                      <div className='text-sm'>
                        <span className='font-medium text-brand-navy'>{a.actorName}</span>{' '}
                        <span className='text-muted-foreground'>{ACTION_LABELS[a.action]}</span>
                        {a.action === 'STATUS_CHANGE' && a.toStatus && (
                          <span className='text-muted-foreground'>
                            {' '}
                            to{' '}
                            <span className='text-brand-navy font-medium'>
                              {STATUS_LABELS[a.toStatus]}
                            </span>
                          </span>
                        )}
                      </div>
                      {detail && (
                        <blockquote className='mt-1 border-l-4 border-blue-400 pl-3 text-sm italic whitespace-pre-wrap text-brand-navy/90'>
                          {detail}
                        </blockquote>
                      )}
                      <time className='text-xs text-muted-foreground'>
                        {format(new Date(a.createdAt), "MMM d 'at' h:mm a")}
                      </time>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className='space-y-4'>
          {(SEND_BACKABLE.has(t.status) ||
            SEND_TO_DEV_FROM.has(t.status) ||
            ENGINEERING_STATUS_MANAGEABLE_FROM.has(t.status)) && (
            <Card className='border-gray-200'>
              <CardHeader>
                <CardTitle className='text-base flex items-center gap-2'>
                  <Shield className='h-5 w-5 text-brand-blue' />
                  Clever Benefits actions
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-2'>
                {SEND_TO_DEV_FROM.has(t.status) && (
                  <Button
                    className='w-full'
                    onClick={() => {
                      setSendToDevCategory(t.engineeringCategory ?? 'ON_CALL');
                      setSendToDevOpen(true);
                    }}
                    disabled={sendToDev.isPending}
                  >
                    Send to dev
                  </Button>
                )}
                {ENGINEERING_STATUS_MANAGEABLE_FROM.has(t.status) && (
                  <div className='space-y-2'>
                    <Label>Development status</Label>
                    <Select
                      value={t.status}
                      disabled={updateStatus.isPending}
                      onValueChange={(status) => {
                        if (status !== t.status) {
                          updateStatus.mutate({id: t.id, status: status as EngineeringStatus});
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {t.status === 'SENT_TO_ENGINEERING' && (
                          <SelectItem value='SENT_TO_ENGINEERING' disabled>
                            {STATUS_LABELS.SENT_TO_ENGINEERING}
                          </SelectItem>
                        )}
                        {ENGINEERING_STATUS_OPTIONS.map((status) => (
                          <SelectItem key={status} value={status}>
                            {STATUS_LABELS[status]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {SEND_BACKABLE.has(t.status) && (
                  <Button
                    variant='destructive'
                    className='w-full'
                    onClick={() => setSendBackOpen(true)}
                    disabled={sendBack.isPending}
                  >
                    Send back to client admin
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className='text-base'>Details</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3 text-sm'>
              <div>
                <div className='text-xs text-brand-gray'>Tenant</div>
                <div className='font-medium text-brand-navy'>{t.client.name}</div>
              </div>
              <div>
                <div className='text-xs text-brand-gray'>Submitter</div>
                <div>{t.submitter.name ?? t.submitter.email}</div>
                <div className='text-xs text-muted-foreground'>{t.submitter.email}</div>
              </div>
              <div>
                <div className='text-xs text-brand-gray'>Submitter role</div>
                <div>{t.submitterRole}</div>
              </div>
              <div>
                <div className='text-xs text-brand-gray'>Category</div>
                {t.status === 'SENT_TO_CLEVER_BENEFITS' ? (
                  <Select
                    value={t.engineeringCategory ?? ''}
                    disabled={updateCategory.isPending}
                    onValueChange={(v) =>
                      id &&
                      updateCategory.mutate({
                        id,
                        engineeringCategory: v as 'PROJECT' | 'ON_CALL',
                      })
                    }
                  >
                    <SelectTrigger className='h-8 mt-1'>
                      <SelectValue placeholder='Not set' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='PROJECT'>Project</SelectItem>
                      <SelectItem value='ON_CALL'>On-Call</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div>
                    {t.engineeringCategory === 'ON_CALL'
                      ? 'On-Call'
                      : t.engineeringCategory === 'PROJECT'
                        ? 'Project'
                        : '—'}
                  </div>
                )}
              </div>
              <div>
                <div className='text-xs text-brand-gray'>Asana</div>
                {t.asanaTaskUrl ? (
                  <a
                    href={t.asanaTaskUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='text-brand-blue inline-flex items-center gap-1'
                  >
                    Open task <ExternalLink className='h-3 w-3' />
                  </a>
                ) : (
                  <span className='text-muted-foreground'>—</span>
                )}
              </div>
              <div>
                <div className='text-xs text-brand-gray'>Created</div>
                <div>{format(new Date(t.createdAt), "MMM d, yyyy 'at' h:mm a")}</div>
              </div>
              <div>
                <div className='text-xs text-brand-gray'>Last updated</div>
                <div>{format(new Date(t.updatedAt), "MMM d, yyyy 'at' h:mm a")}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send back to client admin</DialogTitle>
            <DialogDescription>
              The ticket returns to the client-admin escalation queue. Your reason is shown to the
              admins so they know what to clarify.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label>Reason</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={4000}
              placeholder='What clarification do you need?'
            />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setSendBackOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => id && sendBack.mutate({id, reason: reason.trim()})}
              disabled={!reason.trim() || sendBack.isPending}
            >
              {sendBack.isPending ? 'Sending…' : 'Send back'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendToDevOpen} onOpenChange={setSendToDevOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send to dev</DialogTitle>
            <DialogDescription>
              Moves the ticket to the dev backlog. Sprint planning happens externally
              (Asana / dev team). The submitter and tenant admins are notified.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label>Category</Label>
              <Select
                value={sendToDevCategory}
                onValueChange={(v) => setSendToDevCategory(v as 'PROJECT' | 'ON_CALL')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='PROJECT'>Project</SelectItem>
                  <SelectItem value='ON_CALL'>On-Call</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label>Notes for dev (optional)</Label>
              <Textarea
                value={sendToDevNotes}
                onChange={(e) => setSendToDevNotes(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder='Anything dev should know when picking this up.'
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setSendToDevOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                id &&
                sendToDev.mutate({
                  id,
                  engineeringCategory: sendToDevCategory,
                  notes: sendToDevNotes.trim() || undefined,
                })
              }
              disabled={sendToDev.isPending}
            >
              {sendToDev.isPending ? 'Sending…' : 'Send to dev'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
